import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, Star, Shield, Award } from 'lucide-react';
import Logo from './Logo';

/**
 * Premium Footer Component
 * Luxury-grade footer with sophisticated styling and professional information architecture
 * Designed for high-end development firm positioning
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Scroll to top function for internal navigation
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle navigation with scroll to top
  const handleNavigation = (path) => {
    setTimeout(scrollToTop, 100);
  };

  const navigationLinks = [
    { name: 'Home', path: '/' },
    { name: 'Legacy', path: '/about' },
    { name: 'Craft', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Method', path: '/process' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    'PREMIUM WOOD FRAMING',
    'ADVANCED STRUCTURAL STEEL',
    'PRISTINE DEMOLITION',
    'PREMIUM SIDING INSTALLATION',
    'ARCHITECTURAL INTEGRATION',
    'ELITE PROJECT MANAGEMENT'
  ];

  const serviceAreas = [
    { name: 'Paradise Valley', description: 'Luxury Estates' },
    { name: 'North Scottsdale', description: 'Custom Homes' },
    { name: 'Arcadia', description: 'Historic Elegance' },
    { name: 'Camelback Corridor', description: 'Modern Living' }
  ];

  const credentials = [
    { icon: Shield, text: 'CR-7 Licensed', detail: 'Residential & Commercial' },
    { icon: Award, text: 'Fully Insured', detail: '$2M+ Coverage' },
    { icon: Star, text: '25+ Years', detail: 'Proven Excellence' }
  ];

  return (
    <footer className="relative bg-gray-900 text-white overflow-hidden">
      {/* Architectural Top Beam */}
      <div className="relative">
        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-px bg-gradient-to-r from-amber-400 to-amber-500 shadow-lg shadow-amber-500/50"></div>
      </div>

      <div className="relative z-10">
        {/* Centered Logo Section */}
        <div className="max-w-8xl mx-auto px-6 lg:px-8 pt-16 pb-12">
          <div className="text-center">
            <div className="mb-8 transform hover:scale-105 transition-all duration-500 inline-block">
              <Logo size="large" />
            </div>
            
            <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
              Arizona's most distinguished structural framing specialists, creating the structural foundation for 
              the state's most prestigious custom homes since 2018.
            </p>
          </div>
        </div>

        {/* Architectural Navigation & Content Grid */}
        <div className="max-w-8xl mx-auto px-6 lg:px-8 pb-16">
          <div className="grid lg:grid-cols-3 gap-16">
            
            {/* Navigation Links */}
            <div className="text-center lg:text-left">
              <h3 className="text-lg font-light tracking-widest uppercase text-amber-400 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Navigate
              </h3>
              <div className="space-y-4">
                {navigationLinks.map((link, idx) => (
                  <div key={idx}>
                    <Link 
                      to={link.path}
                      onClick={handleNavigation}
                      className="block text-white hover:text-amber-400 transition-all duration-500 text-lg font-light tracking-wide group relative"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      <span className="uppercase">{link.name}</span>
                      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-amber-400 to-amber-500 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Services */}
            <div className="text-center">
              <h3 className="text-lg font-light tracking-widest uppercase text-amber-400 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Excellence
              </h3>
              <div className="space-y-4">
                {services.map((service, idx) => (
                  <div key={idx} className="group">
                    <div className="text-white text-sm font-light tracking-wide leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {service}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact & Credentials */}
            <div className="text-center lg:text-right">
              <h3 className="text-lg font-light tracking-widest uppercase text-amber-400 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Connect
              </h3>
              
              {/* Refined Contact Information */}
              <div className="space-y-6 mb-10">
                <a 
                  href="tel:602-448-1723"
                  className="block text-white hover:text-amber-400 transition-all duration-300 group"
                >
                  <div className="text-sm text-gray-400 font-light tracking-wide uppercase mb-1">Direct Line</div>
                  <div className="text-xl font-light" style={{ fontFamily: 'Inter, sans-serif' }}>602.448.1723</div>
                </a>
                
                <a 
                  href="mailto:ceballos.4@outlook.com"
                  className="block text-white hover:text-amber-400 transition-all duration-300 group"
                >
                  <div className="text-sm text-gray-400 font-light tracking-wide uppercase mb-1">Email</div>
                  <div className="text-lg font-light" style={{ fontFamily: 'Inter, sans-serif' }}>ceballos.4@outlook.com</div>
                </a>
              </div>

              {/* Architectural Credentials */}
              <div className="space-y-3">
                {credentials.map((credential, idx) => (
                  <div key={idx} className="flex items-center justify-center lg:justify-end space-x-3 text-sm text-amber-400 font-light tracking-wide">
                    <credential.icon className="h-4 w-4" />
                    <span>{credential.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Architectural Bottom Section */}
        <div className="border-t border-amber-500/20">
          <div className="max-w-8xl mx-auto px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
              <div className="text-gray-400 text-center lg:text-left">
                <p className="text-sm font-light tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                  &copy; {currentYear} Ceballos Total Framing LLC. All rights reserved.
                </p>
                <p className="text-xs mt-2 font-light tracking-wider text-amber-400/60">
                  Arizona's Premier Structural Specialists Since 2018
                </p>
              </div>
              
              <div className="flex items-center space-x-8 text-xs font-light tracking-widest text-amber-400">
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                  <span>CR-7 LICENSED</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                  <span>FULLY INSURED</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                  <span>PROVEN EXCELLENCE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;